import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import {
  ApiError,
  authenticate,
  challengeMfa,
  getSession,
  reauthenticate,
  refreshSession,
  revokeSession,
  useApiClient,
  verifyMfa,
  type MfaChallenge,
  type Session,
} from '@/api'
import { t } from '@/i18n'
import { describeApiError } from '@/i18n/errors'

/**
 * Session state.
 *
 * Four things happen here, and each is a security decision rather than an implementation detail.
 *
 * 1. **A password never yields a usable session.** Design §11.4 puts this whole surface behind
 *    mandatory MFA, and the core enforces it globally in
 *    `Api/Security/Mfa/SecondFactorEnforcementSubscriber`: a token from `POST /auth/token` carries
 *    `mfa: false`, and *everything* except `/auth/me`, `/auth/mfa/*`, `/auth/refresh` and
 *    `/auth/revoke` answers it `403 MFA_REQUIRED`. So the token this store holds after a password
 *    is a *partial* token, modelled as its own state. `403 MFA_REQUIRED` on `/api/v1/assets` is
 *    the system working, not a fault to route around, and nothing here retries past it.
 *
 * 2. **The access token lives in memory only.** A refresh token is persisted so a reload does not
 *    log you out, but the short-lived credential that authorises requests never touches storage.
 *    An XSS that can read `localStorage` gets a refresh token it must exchange over the network —
 *    observable and revocable — rather than a bearer token it can use silently.
 *
 * 3. **Only a verified token is persisted as resumable.** The refresh token is written after the
 *    second factor, never after the password stage. `Session.RefreshRequest` says the `mfa` claim
 *    is *carried over* from the current access token and never re-asserted, so persisting a
 *    partial session would give a reload something it can refresh forever and never elevate.
 *
 * 4. **Re-authentication is separate from the session and scoped to one capability.** A ticket
 *    authorises one capability on one asset, once, for a few minutes. It is never stored, never
 *    refreshed, and never widens what the session may do.
 */

const REFRESH_STORAGE_KEY = 'oui-observe.refresh'

/**
 * `anonymous` → `challenged` → `authenticated`.
 *
 * `challenged` is a session that holds a real access token which the core will not accept for any
 * asset. It exists as a distinct state so a screen cannot treat "logged in" as a boolean and get
 * it wrong.
 */
export type SessionStatus = 'anonymous' | 'challenged' | 'authenticated'

/** A pending re-auth prompt, held while a modal collects the factor. */
export interface ReauthRequest {
  capability: string
  assetId: string | null
  label: string
}

/**
 * What `POST /api/v1/auth/token` really returns.
 *
 * The generated client types this response as `{ token: string }`, which is Lexik's default body
 * and **not** what the core sends. `Api/Security/Jwt/AuthenticationSuccessSubscriber` replaces it
 * with the OAuth2 envelope below; the core also documents that envelope, in
 * `Api/OpenApi/AuthenticationOpenApiFactory` — but LexikJWTAuthenticationBundle's own OpenAPI
 * factory decorates *outside* it and overwrites the path item, so the published specification
 * describes the body the server does not send. That is a defect in the core's specification and it
 * is reported there, not patched here.
 *
 * Coding to the documented-but-wrong shape would mean a login screen that cannot log in, so this
 * interface describes the real response. `token` is read as a fallback because the subscriber
 * keeps it for compatibility, and reading it costs nothing.
 */
interface LoginEnvelope {
  access_token?: string
  token?: string
  token_type?: string
  expires_in?: number | null
  refresh_token?: string
  refresh_expires_in?: number
  mfa?: boolean
  mfa_required?: boolean
  mfa_enrolled?: boolean
  /** Where the core says to go next: `/api/v1/auth/mfa/verify`, or `/api/v1/auth/mfa/enrol`. */
  next?: string
}

const readStoredRefreshToken = (): string | null => {
  try {
    return globalThis.localStorage?.getItem(REFRESH_STORAGE_KEY) ?? null
  } catch {
    // A browser with storage disabled is a browser where the session ends with the tab. That is a
    // worse experience, not a broken one, and it must not be a crash on the login screen.
    return null
  }
}

const writeStoredRefreshToken = (value: string | null): void => {
  try {
    if (value === null) globalThis.localStorage?.removeItem(REFRESH_STORAGE_KEY)
    else globalThis.localStorage?.setItem(REFRESH_STORAGE_KEY, value)
  } catch {
    /* see above */
  }
}

export const useAuthStore = defineStore('auth', () => {
  // Resolved once, during store setup, where an injection context exists. Resolving it inside an
  // action would call `inject()` outside `setup()` — which works only by accident of the fallback.
  const api = useApiClient()

  const accessToken = ref<string | null>(null)
  const refreshTokenValue = ref<string | null>(readStoredRefreshToken())
  const session = ref<Session | null>(null)
  const challenge = ref<MfaChallenge | null>(null)
  const pendingEmail = ref<string | null>(null)

  /**
   * True once the second factor has been accepted for the token currently held.
   *
   * Tracked separately from "we have a token", because the two are genuinely different and the
   * whole of design §11.4 lives in the gap between them.
   */
  const secondFactorPresented = ref(false)

  /** The core said this account has no enrolled factor, so it cannot complete a sign-in at all. */
  const enrolmentRequired = ref(false)

  const busy = ref(false)
  const error = ref<string | null>(null)

  /** Set while a destructive action waits for a fresh factor. Drives the re-auth modal. */
  const reauthRequest = ref<ReauthRequest | null>(null)

  const status = computed<SessionStatus>(() => {
    if (accessToken.value === null) return 'anonymous'
    return secondFactorPresented.value ? 'authenticated' : 'challenged'
  })

  const isAuthenticated = computed(() => status.value === 'authenticated')

  /** A reload can resume a session from the refresh token alone. */
  const canResume = computed(() => accessToken.value === null && refreshTokenValue.value !== null)

  const user = computed(() => session.value)
  const roles = computed<string[]>(() =>
    (session.value?.roles ?? []).filter((role): role is string => typeof role === 'string'),
  )
  const permissions = computed<string[]>(() =>
    (session.value?.permissions ?? []).filter((entry): entry is string => typeof entry === 'string'),
  )

  const hasRole = (role: string): boolean => roles.value.includes(role)
  const can = (permission: string): boolean => permissions.value.includes(permission)

  const clear = (): void => {
    accessToken.value = null
    refreshTokenValue.value = null
    session.value = null
    challenge.value = null
    pendingEmail.value = null
    reauthRequest.value = null
    secondFactorPresented.value = false
    enrolmentRequired.value = false
    writeStoredRefreshToken(null)
  }

  /**
   * Adopt the credentials from a `Session` response.
   *
   * `mfa` on the response is authoritative: the core computes it from the claim in the token it
   * just minted. The client never decides it for itself.
   */
  const applySession = (next: Session): void => {
    if (typeof next.access_token === 'string' && next.access_token !== '') {
      accessToken.value = next.access_token
    }

    session.value = next
    secondFactorPresented.value = next.mfa === true

    if (typeof next.refresh_token === 'string' && next.refresh_token !== '') {
      refreshTokenValue.value = next.refresh_token
      // Only a verified session is worth resuming — see the file header, point 3.
      if (next.mfa === true) writeStoredRefreshToken(next.refresh_token)
    }
  }

  /**
   * Stage one: email and password.
   *
   * Returns the state that resulted, which for a correctly configured core is always
   * `'challenged'`. A caller that treats a resolved promise as "signed in" is wrong, and the
   * return type is what says so.
   */
  const login = async (email: string, password: string): Promise<SessionStatus> => {
    busy.value = true
    error.value = null
    enrolmentRequired.value = false

    try {
      // `noRefresh` because a rejected password answers 401, and the client's refresh-and-replay
      // would otherwise fire against a session that does not exist yet. (The specification also
      // marks this operation as requiring a bearer token, which it does not — reported to core.)
      const envelope = (await api.call(authenticate, {
        body: { email, password },
        noRefresh: true,
      })) as unknown as LoginEnvelope

      const token = envelope.access_token ?? envelope.token
      if (typeof token !== 'string' || token === '') {
        throw new ApiError(500, 'INTERNAL', 'The core returned no access token.')
      }

      accessToken.value = token
      pendingEmail.value = email
      // Never true at this stage: `mfa` is false on a password-only token by construction. Read
      // rather than assumed, so a core that ever stops requiring a factor is believed.
      secondFactorPresented.value = envelope.mfa === true

      if (typeof envelope.refresh_token === 'string') {
        refreshTokenValue.value = envelope.refresh_token
      }

      if (secondFactorPresented.value) {
        await loadSession()
        return 'authenticated'
      }

      enrolmentRequired.value = envelope.mfa_enrolled === false
      if (enrolmentRequired.value) {
        // Rendered by LoginView in a `role="alert"`, so it is read aloud — it has to be in the
        // reader's language. Translated at the moment it is raised rather than held as a key,
        // because that is the shape the rest of `error` already has.
        error.value = t('auth.enrolmentRequired')
        return 'challenged'
      }

      // Ask what may be presented. The token from stage one is exactly enough to reach this
      // endpoint and nothing else.
      challenge.value = await api.call(challengeMfa)
      return 'challenged'
    } catch (caught) {
      error.value = describeApiError(caught, 'errors.unknown')
      throw caught
    } finally {
      busy.value = false
    }
  }

  /**
   * Stage two: present the second factor.
   *
   * `method` defaults to the first entry of the challenge's `methods` list, which is the order the
   * core states as its preference.
   */
  const submitMfa = async (response: string, method?: string): Promise<void> => {
    if (accessToken.value === null) throw new Error('There is no session to add a factor to.')

    busy.value = true
    error.value = null

    try {
      const chosen = method ?? preferredMethod.value
      const result = await api.call(verifyMfa, {
        body: { method: chosen, response },
        noRefresh: true,
      })

      if (result.verified !== true) {
        // A core that answers a completed challenge with an unverified challenge is a core we do
        // not understand. Failing here beats looping the user through the same form.
        throw new ApiError(401, 'UNAUTHORIZED', 'The challenge was not accepted.')
      }

      if (typeof result.access_token === 'string' && result.access_token !== '') {
        accessToken.value = result.access_token
      }

      secondFactorPresented.value = true
      challenge.value = null
      pendingEmail.value = null

      // The elevated token is the first one that can reach anything, so this is also the first
      // moment the refresh token is worth keeping across a reload.
      if (refreshTokenValue.value !== null) writeStoredRefreshToken(refreshTokenValue.value)

      await loadSession()
    } catch (caught) {
      error.value = describeApiError(caught, 'errors.unknown')
      throw caught
    } finally {
      busy.value = false
    }
  }

  /** The factor the core would rather we used. `totp` only as a last resort — it is the fallback. */
  const preferredMethod = computed<string>(() => {
    const methods = (challenge.value?.methods ?? []).filter(
      (entry): entry is string => typeof entry === 'string' && entry !== '',
    )
    return methods[0] ?? 'totp'
  })

  const availableMethods = computed<string[]>(() =>
    (challenge.value?.methods ?? []).filter(
      (entry): entry is string => typeof entry === 'string' && entry !== '',
    ),
  )

  const cancelChallenge = (): void => {
    clear()
    error.value = null
  }

  /**
   * Exchange the refresh token. Returns the new access token, or null when the session is over.
   *
   * Wired into the API client as its `refresh` hook, which de-duplicates concurrent callers — so a
   * screen firing six requests against an expired token performs one refresh, not six.
   */
  const refresh = async (): Promise<string | null> => {
    const token = refreshTokenValue.value
    if (!token) return null

    try {
      const next = await api.call(refreshSession, {
        body: { refresh_token: token },
        // Without this a failing refresh would try to refresh itself, forever.
        noRefresh: true,
      })

      applySession(next)
      return typeof next.access_token === 'string' ? next.access_token : null
    } catch {
      clear()
      return null
    }
  }

  /**
   * Resume a session on page load, before the first screen renders.
   *
   * A resumed session is only usable if the core carried the `mfa` claim over — which it does only
   * while the previous access token was still valid. When it did not, this leaves the store in
   * `challenged`, and the login view collects the factor again. That is the documented behaviour
   * of `POST /auth/refresh`, not a degradation to work around.
   */
  const resume = async (): Promise<boolean> => {
    if (isAuthenticated.value) return true
    if (!refreshTokenValue.value) return false

    const token = await refresh()
    if (!token) return false

    if (!secondFactorPresented.value) {
      challenge.value = await api.call(challengeMfa).catch(() => null)
      return false
    }

    return true
  }

  const loadSession = async (): Promise<void> => {
    try {
      const next = await api.call(getSession)
      session.value = next
      secondFactorPresented.value = next.mfa === true
    } catch (caught) {
      if (caught instanceof ApiError && caught.isUnauthorized) clear()
      else throw caught
    }
  }

  const logout = async (): Promise<void> => {
    try {
      if (accessToken.value) {
        await api.call(revokeSession, {
          body: { refresh_token: refreshTokenValue.value ?? null },
          noRefresh: true,
        })
      }
    } catch {
      // A failed revocation must not strand somebody in a session they asked to leave. The token
      // is dropped locally regardless; the core expires it on its own schedule.
    } finally {
      clear()
    }
  }

  /** End every session for this account — "I have lost my laptop". */
  const logoutEverywhere = async (): Promise<void> => {
    try {
      if (accessToken.value) await api.call(revokeSession, { body: { all: true }, noRefresh: true })
    } catch {
      /* see above */
    } finally {
      clear()
    }
  }

  /** Called by the API client when a refresh fails. */
  const onSessionExpired = (): void => {
    clear()
    error.value = t('auth.sessionExpired')
  }

  // --- re-authentication --------------------------------------------------

  /**
   * Ask for a fresh factor for one capability, and hold the request for the modal to answer.
   *
   * Returns a promise the caller awaits: the destructive action stays paused, in its own call
   * stack, until the person either supplies a factor or cancels. That keeps "the operation was
   * dispatched" and "the person approved it" in one place instead of two.
   */
  let reauthResolve: ((token: string | null) => void) | null = null

  const requestReauth = (request: ReauthRequest): Promise<string | null> => {
    reauthRequest.value = request
    return new Promise<string | null>((resolve) => {
      reauthResolve = resolve
    })
  }

  const completeReauth = async (input: { method: string; response: string }): Promise<void> => {
    const request = reauthRequest.value
    if (!request) return

    busy.value = true
    error.value = null

    try {
      // The core binds the ticket to the capability and the asset, so a ticket obtained for
      // `cache.clear` on one site cannot be spent on `core.update` on another.
      const result = await api.call(reauthenticate, {
        body: {
          method: input.method,
          response: input.response,
          capability: request.capability,
          asset_id: request.assetId,
        },
      })

      reauthRequest.value = null
      reauthResolve?.(result.ticket ?? null)
      reauthResolve = null
    } catch (caught) {
      error.value = describeApiError(caught, 'errors.unknown')
      throw caught
    } finally {
      busy.value = false
    }
  }

  const cancelReauth = (): void => {
    reauthRequest.value = null
    reauthResolve?.(null)
    reauthResolve = null
  }

  return {
    accessToken,
    refreshTokenValue,
    session,
    user,
    challenge,
    pendingEmail,
    secondFactorPresented,
    enrolmentRequired,
    busy,
    error,
    reauthRequest,
    status,
    isAuthenticated,
    canResume,
    roles,
    permissions,
    preferredMethod,
    availableMethods,
    hasRole,
    can,
    login,
    submitMfa,
    cancelChallenge,
    refresh,
    resume,
    loadSession,
    logout,
    logoutEverywhere,
    onSessionExpired,
    requestReauth,
    completeReauth,
    cancelReauth,
    clear,
  }
})
